import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Truck, CheckCircle, Clock, MapPin, 
  MessageSquare, Phone, Mail, Printer, Download, Star, Shield, 
  CreditCard, AlertCircle, DollarSign, Calendar, Users, Tag,
  FileText, Home, Navigation, PhoneCall, Mail as MailIcon,
  ChevronRight, Info, Percent, ShieldCheck, XCircle
} from 'lucide-react';
import Header from '../../components/layout/Header';
import { useAuth } from '../../context/AuthContext';
import { getOrder } from '../../services/api';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrderDetails();
  }, [id, user, navigate]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrder(id);
      
      if (response.success) {
        setOrder(response.data);
      } else {
        setError('Order not found or you do not have permission to view it.');
      }
    } catch (error) {
      // console.error('Error fetching order details:', error);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Parse JSON fields
  const parseFeeBreakdown = (feeBreakdown) => {
    if (!feeBreakdown) return null;
    try {
      return typeof feeBreakdown === 'string' 
        ? JSON.parse(feeBreakdown)
        : feeBreakdown;
    } catch (e) {
      // console.error('Error parsing fee breakdown:', e);
      return null;
    }
  };

  const parseBidHistory = (bidHistory) => {
    if (!bidHistory) return [];
    try {
      return typeof bidHistory === 'string'
        ? JSON.parse(bidHistory)
        : bidHistory;
    } catch (e) {
      // console.error('Error parsing bid history:', e);
      return [];
    }
  };

  // Helper functions
  const getDisplayStatus = (order) => {
    if (!order) return 'unknown';
    if (order.disputed === 1) return 'disputed';
    if (order.status === 'cancelled' || order.status === 'rejected') return 'cancelled';
    if (order.actual_delivery_date || order.delivered_at) return 'delivered';
    if (order.shipped_at) return 'shipped';
    if (order.payment_status === 'paid' && order.status === 'accepted') return 'processing';
    if (order.payment_status === 'pending' && order.status === 'accepted') return 'pending_payment';
    if (order.payment_status === 'pending' && order.status === 'pending') return 'pending_payment';
    return order.status;
  };

  const getStatusInfo = (order) => {
    const status = getDisplayStatus(order);
    switch (status) {
      case 'pending_payment':
        return { 
          icon: <Clock className="w-6 h-6 text-yellow-500" />,
          color: 'bg-yellow-100 text-yellow-800',
          text: 'Pending Payment',
          description: 'Waiting for payment confirmation'
        };
      case 'pending':
        return { 
          icon: <Clock className="w-6 h-6 text-yellow-500" />,
          color: 'bg-yellow-100 text-yellow-800',
          text: 'Pending',
          description: 'Waiting for seller confirmation'
        };
      case 'processing':
      case 'accepted':
        return { 
          icon: <Package className="w-6 h-6 text-blue-500" />,
          color: 'bg-blue-100 text-blue-800',
          text: 'Processing',
          description: 'Seller is preparing your order'
        };
      case 'shipped':
        return { 
          icon: <Truck className="w-6 h-6 text-purple-500" />,
          color: 'bg-purple-100 text-purple-800',
          text: 'Shipped',
          description: 'Your order is on the way'
        };
      case 'delivered':
        return { 
          icon: <CheckCircle className="w-6 h-6 text-green-500" />,
          color: 'bg-green-100 text-green-800',
          text: 'Delivered',
          description: 'Your order has been delivered'
        };
      case 'cancelled':
      case 'rejected':
        return { 
          icon: <XCircle className="w-6 h-6 text-red-500" />,
          color: 'bg-red-100 text-red-800',
          text: 'Cancelled',
          description: 'This order has been cancelled'
        };
      case 'disputed':
        return { 
          icon: <AlertCircle className="w-6 h-6 text-red-500" />,
          color: 'bg-red-100 text-red-800',
          text: 'Disputed',
          description: 'This order has a dispute'
        };
      default:
        return { 
          icon: <Package className="w-6 h-6 text-gray-500" />,
          color: 'bg-gray-100 text-gray-800',
          text: 'Unknown',
          description: 'Status unknown'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0';
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  const getTrackingUpdates = (order) => {
    const updates = [];
    
    // Order placed
    updates.push({
      date: order.created_at,
      status: 'order_placed',
      description: 'Order placed',
      icon: <Package className="w-5 h-5 text-blue-500" />
    });
    
    // Status update
    if (order.status === 'accepted') {
      updates.push({
        date: order.updated_at,
        status: 'accepted',
        description: 'Order accepted by seller',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />
      });
    }
    
    // Payment if paid
    if (order.paid_at) {
      updates.push({
        date: order.paid_at,
        status: 'payment_confirmed',
        description: 'Payment confirmed',
        icon: <DollarSign className="w-5 h-5 text-green-500" />
      });
    }
    
    // Meeting time if set
    if (order.meeting_time) {
      updates.push({
        date: order.meeting_time,
        status: 'meeting_scheduled',
        description: 'Meeting scheduled',
        icon: <Calendar className="w-5 h-5 text-purple-500" />
      });
    }
    
    // Shipped if shipped
    if (order.shipped_at) {
      updates.push({
        date: order.shipped_at,
        status: 'shipped',
        description: 'Order shipped',
        icon: <Truck className="w-5 h-5 text-purple-500" />
      });
    }
    
    // Delivered if delivered
    if (order.delivered_at || order.actual_delivery_date) {
      updates.push({
        date: order.delivered_at || order.actual_delivery_date,
        status: 'delivered',
        description: 'Order delivered',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />
      });
    }
    
    return updates;
  };

  const handleContactSeller = () => {
    if (order?.seller_phone) {
      window.location.href = `tel:${order.seller_phone}`;
    }
  };

  const handleMessageSeller = () => {
    navigate(`/messages?seller=${order?.seller_id}`);
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleRaiseDispute = () => {
    navigate(`/disputes/raise?order=${id}`);
  };

  const handleRateOrder = () => {
    navigate(`/orders/${id}/rate`);
  };

  const handlePayNow = () => {
    navigate(`/payment/order/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-64 bg-gray-200 rounded-xl"></div>
                <div className="h-48 bg-gray-200 rounded-xl"></div>
              </div>
              <div className="space-y-4">
                <div className="h-48 bg-gray-200 rounded-xl"></div>
                <div className="h-32 bg-gray-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate('/my-orders')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Orders
          </button>
          
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {error || 'Order Not Found'}
            </h3>
            <p className="text-gray-600 mb-6">
              {error || "The order you're looking for doesn't exist or you don't have permission to view it."}
            </p>
            <button
              onClick={() => navigate('/my-orders')}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Back to My Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order);
  const feeBreakdown = parseFeeBreakdown(order.fee_breakdown);
  const bidHistory = parseBidHistory(order.bid_history);
  const trackingUpdates = getTrackingUpdates(order);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
{/* Back Navigation */}
<button
  onClick={() => navigate('/my-orders')}
  className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
>
  <ArrowLeft className="w-5 h-5 mr-2" />
  Back to Orders
</button>

{/* Order Header */}
<div className="mb-8">
  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
        Order #{order.order_id || order.id}
      </h1>
      <p className="text-gray-600">
        Placed on {formatDate(order.created_at)}
      </p>
    </div>
    <div className="flex items-center gap-4">
      {getDisplayStatus(order) === 'pending_payment' && (
        <button
          onClick={handlePayNow}
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
        >
          <CreditCard className="w-5 h-5 mr-2" />
          Pay Now
        </button>
      )}
      {getDisplayStatus(order) === 'delivered' && !order.review_id && (
        <button
          onClick={handleRateOrder}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
        >
          <Star className="w-5 h-5 mr-2" />
          Rate Order
        </button>
      )}
      <button
        onClick={handlePrintInvoice}
        className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
      >
        <Printer className="w-5 h-5 mr-2" />
        Print
      </button>
    </div>
  </div>

  {/* Status Banner */}
  <div className={`p-6 rounded-2xl ${statusInfo.color} flex items-start gap-4 mb-8`}>
    <div className="mt-1">{statusInfo.icon}</div>
    <div className="flex-1">
      <h3 className="text-lg font-semibold mb-1">{statusInfo.text}</h3>
      <p className="text-sm opacity-90">{statusInfo.description}</p>
      {order.disputed === 1 && (
        <p className="text-sm mt-2">
          Dispute ID: <span className="font-semibold">{order.dispute_id}</span>
        </p>
      )}
    </div>
    {getDisplayStatus(order) === 'processing' && !order.disputed && (
      <button
        onClick={handleRaiseDispute}
        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm flex items-center"
      >
        <AlertCircle className="w-4 h-4 mr-2" />
        Raise Dispute
      </button>
    )}
  </div>
</div>

{/* Tabs */}
<div className="border-b border-gray-200 mb-8">
  <nav className="flex space-x-8 overflow-x-auto">
    {['overview', 'tracking', 'payment', 'documents'].map((tab) => (
      <button
        key={tab}
        onClick={() => setActiveTab(tab)}
        className={`py-3 px-1 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
          activeTab === tab
            ? 'border-orange-500 text-orange-600'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        {tab === 'overview' && 'Overview'}
        {tab === 'tracking' && 'Tracking'}
        {tab === 'payment' && 'Payment'}
        {tab === 'documents' && 'Documents'}
      </button>
    ))}
  </nav>
</div>

{/* Main Content */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
  {/* Left Column - Order Details */}
  <div className="lg:col-span-2 space-y-8">
    {/* Order Items */}
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
        <Package className="w-6 h-6 mr-2 text-gray-400" />
        Order Items
      </h2>
      
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200">
          <div className="col-span-7 font-semibold text-gray-700">Item</div>
          <div className="col-span-2 font-semibold text-gray-700 text-center">Quantity</div>
          <div className="col-span-3 font-semibold text-gray-700 text-right">Price</div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-7">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-gray-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{order.item_name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{order.description}</p>
                  {order.item_category && (
                    <span className="inline-block mt-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                      {order.item_category}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-2 text-center">
              <span className="font-semibold">1</span>
            </div>
            <div className="col-span-3 text-right">
              <div className="font-semibold text-gray-900">
                {formatCurrency(order.amount || order.price)}
              </div>
              {order.original_price && order.amount !== order.original_price && (
                <div className="text-sm text-gray-500 line-through">
                  {formatCurrency(order.original_price)}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Subtotal</span>
            <span className="font-semibold">{formatCurrency(order.amount || order.price)}</span>
          </div>
          {feeBreakdown?.map((fee, index) => (
            <div key={index} className="flex justify-between items-center mt-2">
              <span className="text-gray-700">{fee.description}</span>
              <span className="font-semibold">{formatCurrency(fee.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
            <span className="text-lg font-semibold text-gray-900">Total</span>
            <span className="text-xl font-bold text-orange-600">
              {formatCurrency(order.total_amount || order.amount)}
            </span>
          </div>
        </div>
      </div>
    </div>

    {/* Tracking & Timeline (Visible only in overview tab) */}
    {activeTab === 'overview' && trackingUpdates.length > 0 && (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Clock className="w-6 h-6 mr-2 text-gray-400" />
          Order Timeline
        </h2>
        
        <div className="space-y-6">
          {trackingUpdates
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((update, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    {update.icon}
                  </div>
                  {index < trackingUpdates.length - 1 && (
                    <div className="absolute left-5 top-10 w-0.5 h-10 bg-gray-200"></div>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{update.description}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatDateTime(update.date)}
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>
    )}

    {/* Payment Details (Visible only in payment tab) */}
    {activeTab === 'payment' && (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <CreditCard className="w-6 h-6 mr-2 text-gray-400" />
          Payment Details
        </h2>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-4">Payment Status</h3>
              <div className={`inline-flex items-center px-4 py-2 rounded-full ${
                order.payment_status === 'paid' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  order.payment_status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
              </div>
              {order.paid_at && (
                <p className="text-sm text-gray-600 mt-2">
                  Paid on {formatDate(order.paid_at)}
                </p>
              )}
            </div>
            
            <div className="p-4 bg-gray-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-4">Payment Method</h3>
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-gray-700">
                  {order.payment_method || 'Not specified'}
                </span>
              </div>
              {order.transaction_id && (
                <p className="text-sm text-gray-600 mt-2">
                  Transaction ID: {order.transaction_id}
                </p>
              )}
            </div>
          </div>

          {/* Fee Breakdown */}
          {feeBreakdown && feeBreakdown.length > 0 && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Fee Breakdown</h3>
              </div>
              <div className="p-4">
                {feeBreakdown.map((fee, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
                    <div className="flex items-center">
                      {fee.type === 'service_fee' && <Percent className="w-5 h-5 text-gray-400 mr-3" />}
                      {fee.type === 'delivery_fee' && <Truck className="w-5 h-5 text-gray-400 mr-3" />}
                      {fee.type === 'insurance_fee' && <ShieldCheck className="w-5 h-5 text-gray-400 mr-3" />}
                      <span className="text-gray-700">{fee.description}</span>
                    </div>
                    <span className="font-semibold text-gray-900">{formatCurrency(fee.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Documents Tab */}
    {activeTab === 'documents' && (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <FileText className="w-6 h-6 mr-2 text-gray-400" />
          Order Documents
        </h2>
        
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-blue-500 mr-4" />
                <div>
                  <h4 className="font-semibold text-gray-900">Invoice #{order.order_id}</h4>
                  <p className="text-sm text-gray-600">Generated on {formatDate(order.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button className="p-2 text-gray-500 hover:text-gray-700">
                  <Printer className="w-5 h-5" />
                </button>
                <button className="p-2 text-gray-500 hover:text-gray-700">
                  <Download className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          
          {order.receipt_url && (
            <div className="p-4 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="w-8 h-8 text-green-500 mr-4" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Payment Receipt</h4>
                    <p className="text-sm text-gray-600">Payment confirmation document</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button className="p-2 text-gray-500 hover:text-gray-700">
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Seller Information */}
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
        <Users className="w-6 h-6 mr-2 text-gray-400" />
        Seller Information
      </h2>
      
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{order.seller_name}</h3>
              <p className="text-sm text-gray-600">Member since {formatDate(order.seller_join_date)}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {order.seller_phone && (
              <div className="flex items-center text-gray-700">
                <Phone className="w-5 h-5 mr-3 text-gray-400" />
                <span>{order.seller_phone}</span>
              </div>
            )}
            {order.seller_email && (
              <div className="flex items-center text-gray-700">
                <MailIcon className="w-5 h-5 mr-3 text-gray-400" />
                <span>{order.seller_email}</span>
              </div>
            )}
            {order.seller_location && (
              <div className="flex items-center text-gray-700">
                <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                <span>{order.seller_location}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="border-l border-gray-200 pl-6">
          <h4 className="font-semibold text-gray-900 mb-4">Seller Rating</h4>
          <div className="flex items-center mb-4">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= (order.seller_rating || 0)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="ml-2 font-semibold">
              {order.seller_rating ? order.seller_rating.toFixed(1) : 'N/A'}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            {order.seller_review_count || 0} reviews
          </p>
          
          <div className="space-y-3">
            <button
              onClick={handleContactSeller}
              disabled={!order.seller_phone}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PhoneCall className="w-5 h-5 mr-2" />
              Call Seller
            </button>
            <button
              onClick={handleMessageSeller}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Message Seller
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* Right Column - Order Summary & Actions */}
  <div className="space-y-8">
    {/* Order Summary */}
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Order Summary</h2>
      
      <div className="space-y-4">
        <div className="flex justify-between">
          <span className="text-gray-600">Order ID</span>
          <span className="font-semibold">#{order.order_id || order.id}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Order Date</span>
          <span>{formatDate(order.created_at)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Payment Status</span>
          <span className={`font-semibold ${
            order.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'
          }`}>
            {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
          </span>
        </div>
        
        {order.expected_delivery_date && (
          <div className="flex justify-between">
            <span className="text-gray-600">Expected Delivery</span>
            <span>{formatDate(order.expected_delivery_date)}</span>
          </div>
        )}
        
        {order.actual_delivery_date && (
          <div className="flex justify-between">
            <span className="text-gray-600">Actual Delivery</span>
            <span className="text-green-600 font-semibold">
              {formatDate(order.actual_delivery_date)}
            </span>
          </div>
        )}
        
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between text-lg font-semibold text-gray-900">
            <span>Total Amount</span>
            <span>{formatCurrency(order.total_amount || order.amount)}</span>
          </div>
        </div>
      </div>
    </div>

    {/* Delivery Information */}
    {(order.delivery_address || order.meeting_location) && (
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Truck className="w-6 h-6 mr-2 text-gray-400" />
          Delivery Information
        </h2>
        
        <div className="space-y-4">
          {order.delivery_method === 'pickup' && order.meeting_location && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-orange-500" />
                Pickup Location
              </h3>
              <p className="text-gray-700">{order.meeting_location}</p>
              {order.meeting_time && (
                <p className="text-sm text-gray-600 mt-2">
                  Meeting Time: {formatDateTime(order.meeting_time)}
                </p>
              )}
            </div>
          )}
          
          {order.delivery_method === 'delivery' && order.delivery_address && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Home className="w-5 h-5 mr-2 text-blue-500" />
                Delivery Address
              </h3>
              <p className="text-gray-700">{order.delivery_address}</p>
              {order.delivery_instructions && (
                <p className="text-sm text-gray-600 mt-2">
                  Instructions: {order.delivery_instructions}
                </p>
              )}
            </div>
          )}
          
          {order.tracking_number && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                <Navigation className="w-5 h-5 mr-2 text-purple-500" />
                Tracking Number
              </h3>
              <p className="text-gray-700 font-mono">{order.tracking_number}</p>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Important Notes */}
    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
      <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
        <Info className="w-5 h-5 mr-2" />
        Important Notes
      </h3>
      
      <ul className="space-y-3 text-sm text-blue-800">
        <li className="flex items-start">
          <ChevronRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
          Keep all order documents for reference
        </li>
        <li className="flex items-start">
          <ChevronRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
          Contact seller directly for delivery updates
        </li>
        <li className="flex items-start">
          <ChevronRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
          Raise disputes within 7 days of delivery
        </li>
        <li className="flex items-start">
          <ChevronRight className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
          Rate your experience after delivery
        </li>
      </ul>
    </div>

    {/* Quick Actions */}
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
      
      <div className="space-y-3">
        {getDisplayStatus(order) === 'pending_payment' && (
          <button
            onClick={handlePayNow}
            className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Pay Now
          </button>
        )}
        
        {getDisplayStatus(order) === 'delivered' && !order.review_id && (
          <button
            onClick={handleRateOrder}
            className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center"
          >
            <Star className="w-5 h-5 mr-2" />
            Rate Order
          </button>
        )}
        
        {['processing', 'shipped'].includes(getDisplayStatus(order)) && !order.disputed && (
          <button
            onClick={handleRaiseDispute}
            className="w-full px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center"
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            Raise Dispute
          </button>
        )}
        
        <button
          onClick={handleMessageSeller}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Message Seller
        </button>
        
        <button
          onClick={handlePrintInvoice}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
        >
          <Printer className="w-5 h-5 mr-2" />
          Print Invoice
        </button>
      </div>
    </div>
  </div>
</div>
</div>
</div>
);
};

export default OrderDetail;